import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreatorGridService from "../../../creator/service/CreatorGridService";
import StatsService from "../../../services/StatsGridService";
import { getAdminContentLanguage } from "../../../services/LangPrefs";
import PublishingPolicyDialog from "../../pzl_common/board/PublishingPolicyDialog";
import PuzzleWorkflowStepper from "../../pzl_common/board/PuzzleWorkflowStepper";
import StateStatsCard from "../../pzl_common/board/StateStatsCard";
import { DRAFT, PUBLISHED, READY_TO_REVIEW, REVIEWED } from "./AdminGridBoardService";
import Logger from "../../../services/Logger";
import { gridBoardDetailPath } from "./gridBoardDetailPath";
import Step0SelectDraft from "./Step0_SelectDraft";

const DISPLAY_STATES = [DRAFT, READY_TO_REVIEW, REVIEWED, PUBLISHED];
const GRID_PUZZLE_TYPES = ["WORD_SEARCH(mini)", "WORD_SEARCH(12x12)", "HIDDEN_MESSAGE"];
/** Map legacy stats keys from older API responses. */
const GRID_STATS_TYPE_ALIASES = { "WORD_SEARCH(9x9)": "WORD_SEARCH(mini)" };
const STATS_VIEW_MODE_KEY = "admin.grid.statsViewMode";

function mergeStateStats(stats, stateKeys) {
    const merged = {
        countByDifficulty: { total: 0, EASY: 0, MEDIUM: 0, HARD: 0 },
        countByType: {},
        countByTag: {}
    };
    stateKeys.forEach((key) => {
        const d = stats[key];
        if (!d) return;
        const diff = d.countByDifficulty || {};
        merged.countByDifficulty.total += diff.total ?? 0;
        merged.countByDifficulty.EASY += diff.EASY ?? 0;
        merged.countByDifficulty.MEDIUM += diff.MEDIUM ?? 0;
        merged.countByDifficulty.HARD += diff.HARD ?? 0;
        Object.entries(d.countByType || {}).forEach(([type, fp]) => {
            const typeKey = GRID_STATS_TYPE_ALIASES[type] || type;
            if (!merged.countByType[typeKey]) merged.countByType[typeKey] = { PAID: 0, FREE: 0 };
            const o = typeof fp === "object" && fp ? fp : {};
            merged.countByType[typeKey].PAID += o.PAID ?? 0;
            merged.countByType[typeKey].FREE += o.FREE ?? 0;
        });
        Object.entries(d.countByTag || {}).forEach(([tag, count]) => {
            merged.countByTag[tag] = (merged.countByTag[tag] || 0) + (typeof count === "number" ? count : 0);
        });
    });
    return merged;
}

const steps = [
    { label: "Draft", state: DRAFT },
    { label: "Ready to review", state: READY_TO_REVIEW },
    { label: "Reviewed", state: REVIEWED },
    { label: "Published", state: PUBLISHED },
];

// localStorage keys for filter options
const FILTER_TYPE_KEY = "admin.grid.filter.type";
const FILTER_PAYMENT_KEY = "admin.grid.filter.payment";
const FILTER_TAGS_KEY = "admin.grid.filter.tags";
const FILTER_LEVEL_KEY = "admin.grid.filter.level";
const FILTER_KEYWORD_KEY = "admin.grid.filter.keyword";
const FILTER_SIZE_KEY = "admin.grid.filter.size";
const GRID_SIZE_ALL = "all";
const GRID_SIZE_MINI = "mini";
const GRID_SIZE_CLASSIC = "classic";
const VALID_GRID_SIZES = [GRID_SIZE_ALL, GRID_SIZE_MINI, GRID_SIZE_CLASSIC];

function normalizeStoredGridSize(stored) {
    if (stored === "9" || stored === 9) return GRID_SIZE_MINI;
    if (stored === "12" || stored === 12) return GRID_SIZE_CLASSIC;
    return VALID_GRID_SIZES.includes(stored) ? stored : GRID_SIZE_ALL;
}

// Helper functions to load filter options from localStorage
const loadFilterType = () => {
    try {
        const stored = localStorage.getItem(FILTER_TYPE_KEY);
        return stored || "all";
    } catch (error) {
        Logger.error('Error reading filter type from localStorage:', error);
        return "all";
    }
};

const loadFilterPayment = () => {
    try {
        const stored = localStorage.getItem(FILTER_PAYMENT_KEY);
        if (stored === null) return null;
        if (stored === "all") return null;
        return stored === "true" || stored === "free";
    } catch (error) {
        Logger.error('Error reading filter payment from localStorage:', error);
        return null;
    }
};

const loadFilterTags = () => {
    try {
        const stored = localStorage.getItem(FILTER_TAGS_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        Logger.error('Error reading filter tags from localStorage:', error);
        return [];
    }
};

const loadFilterLevel = () => {
    try {
        const stored = localStorage.getItem(FILTER_LEVEL_KEY);
        return stored || "all";
    } catch (error) {
        Logger.error('Error reading filter level from localStorage:', error);
        return "all";
    }
};

const loadFilterSize = () => {
    try {
        return normalizeStoredGridSize(localStorage.getItem(FILTER_SIZE_KEY));
    } catch (error) {
        Logger.error('Error reading filter size from localStorage:', error);
        return GRID_SIZE_ALL;
    }
};

const loadFilterKeyword = () => {
    try {
        const stored = localStorage.getItem(FILTER_KEYWORD_KEY);
        return stored != null ? String(stored) : "";
    } catch (error) {
        Logger.error('Error reading filter keyword from localStorage:', error);
        return "";
    }
};

// Helper functions to save filter options to localStorage
const saveFilterType = (type) => {
    try {
        localStorage.setItem(FILTER_TYPE_KEY, type || "all");
    } catch (error) {
        Logger.error('Error saving filter type to localStorage:', error);
    }
};

const saveFilterPayment = (payment) => {
    try {
        if (payment === null) {
            localStorage.setItem(FILTER_PAYMENT_KEY, "all");
        } else {
            localStorage.setItem(FILTER_PAYMENT_KEY, payment === true ? "free" : "paid");
        }
    } catch (error) {
        Logger.error('Error saving filter payment to localStorage:', error);
    }
};

const saveFilterTags = (tags) => {
    try {
        if (tags && Array.isArray(tags) && tags.length > 0) {
            localStorage.setItem(FILTER_TAGS_KEY, JSON.stringify(tags));
        } else {
            localStorage.removeItem(FILTER_TAGS_KEY);
        }
    } catch (error) {
        Logger.error('Error saving filter tags to localStorage:', error);
    }
};

const saveFilterLevel = (level) => {
    try {
        localStorage.setItem(FILTER_LEVEL_KEY, level || "all");
    } catch (error) {
        Logger.error('Error saving filter level to localStorage:', error);
    }
};

const saveFilterSize = (size) => {
    try {
        localStorage.setItem(FILTER_SIZE_KEY, normalizeStoredGridSize(size));
    } catch (error) {
        Logger.error('Error saving filter size to localStorage:', error);
    }
};

const saveFilterKeyword = (keyword) => {
    try {
        if (keyword != null && String(keyword).trim() !== "") {
            localStorage.setItem(FILTER_KEYWORD_KEY, String(keyword).trim());
        } else {
            localStorage.removeItem(FILTER_KEYWORD_KEY);
        }
    } catch (error) {
        Logger.error('Error saving filter keyword to localStorage:', error);
    }
};

export default function AdminGridBoardMain() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Initialize activeStep from URL parameter if present, otherwise default to 0
    const getInitialStep = () => {
        const activeStepParam = searchParams.get('activeStep');
        if (activeStepParam !== null) {
            const stepIndex = parseInt(activeStepParam, 10);
            if (stepIndex >= 0 && stepIndex < steps.length) {
                return stepIndex;
            }
        }
        return 0;
    };
    
    const [activeStep, setActiveStep] = useState(getInitialStep);
    const selectedGenreId = "all";

    // State for group-by-state stats
    const [groupByStateStats, setGroupByStateStats] = useState(null);
    const [groupByStateLoading, setGroupByStateLoading] = useState(false);
    // Fallback tags for view 2 when group-by-state API doesn't return countByTag
    const [view2TagsCount, setView2TagsCount] = useState(null);

    // State for type, tags, level, and free/paid filters - load from localStorage on mount
    const [selectedType, setSelectedType] = useState(() => {
        try {
            const typeParam = new URLSearchParams(window.location.search).get("type");
            const normalized = typeParam ? String(typeParam).trim().toUpperCase() : "";
            if (normalized === "HIDDEN_MESSAGE" || normalized === "WORD_SEARCH") {
                saveFilterType(normalized);
                return normalized;
            }
            if (normalized === "ALL") {
                saveFilterType("all");
                return "all";
            }
        } catch (error) {
            Logger.error("Error reading type from URL:", error);
        }
        return loadFilterType();
    });
    const [selectedTags, setSelectedTags] = useState(() => {
        try {
            const tagParam = new URLSearchParams(window.location.search).get("tag");
            if (tagParam && tagParam.trim()) {
                const tags = [tagParam.trim()];
                saveFilterTags(tags);
                return tags;
            }
        } catch (error) {
            Logger.error("Error reading tag from URL:", error);
        }
        return loadFilterTags();
    });
    const [availableTags, setAvailableTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(loadFilterLevel);
    const [selectedFree, setSelectedFree] = useState(loadFilterPayment);
    const [savedKeyword, setSavedKeyword] = useState(loadFilterKeyword);
    const [selectedGridSize, setSelectedGridSize] = useState(loadFilterSize);

    // State for policy dialog
    const [policyDialogOpen, setPolicyDialogOpen] = useState(false);

    const [statsViewMode, setStatsViewMode] = useState(() => {
        try {
            const v = localStorage.getItem(STATS_VIEW_MODE_KEY);
            if (v === "1" || v === "2") return parseInt(v, 10);
        } catch (e) {}
        return 1;
    });
    const setStatsViewModeAndSave = (mode) => {
        setStatsViewMode(mode);
        try {
            localStorage.setItem(STATS_VIEW_MODE_KEY, String(mode));
        } catch (e) {}
    };

    // When leaving DRAFT step, switch to view 1 (4 cards) without overwriting localStorage
    useEffect(() => {
        if (activeStep !== 0 && statsViewMode === 2) {
            setStatsViewMode(1);
        }
    }, [activeStep, statsViewMode]);

    // When returning to DRAFT, restore view mode from localStorage
    useEffect(() => {
        if (activeStep === 0) {
            try {
                const v = localStorage.getItem(STATS_VIEW_MODE_KEY);
                if (v === "1" || v === "2") setStatsViewMode(parseInt(v, 10));
            } catch (e) {}
        }
    }, [activeStep]);

    // Fetch group-by-state stats on component mount
    useEffect(() => {
        const fetchGroupByState = async () => {
            setGroupByStateLoading(true);
            try {
                const language = getAdminContentLanguage();
                const result = await StatsService.countByStateGrouped(null, language);
                setGroupByStateStats(result || {});
            } catch (error) {
                Logger.error("Error fetching group-by-state stats:", error);
                setGroupByStateStats({});
            } finally {
                setGroupByStateLoading(false);
            }
        };

        fetchGroupByState();
    }, []);

    // Fetch tags for view 2 (READY_TO_REVIEW + REVIEWED + PUBLISHED) - used when group-by-state doesn't include countByTag
    useEffect(() => {
        if (statsViewMode !== 2 || activeStep !== 0) {
            setView2TagsCount(null);
            return;
        }
        const fetchView2Tags = async () => {
            try {
                const language = getAdminContentLanguage();
                const [rtr, rev, pub] = await Promise.all([
                    CreatorGridService.getGridTags(language, READY_TO_REVIEW),
                    CreatorGridService.getGridTags(language, REVIEWED),
                    CreatorGridService.getGridTags(language, PUBLISHED)
                ]);
                const mergeTagCounts = (acc, list) => {
                    const arr = Array.isArray(list) ? list : [];
                    arr.forEach((item) => {
                        const name = typeof item === "string" ? item : (item?.tag ?? item?.name ?? String(item ?? ""));
                        const count = typeof item === "object" && item != null && typeof item.count === "number" ? item.count : 0;
                        if (name && count > 0) acc[name] = (acc[name] || 0) + count;
                    });
                    return acc;
                };
                const merged = [rtr, rev, pub].reduce((acc, list) => mergeTagCounts(acc, list), {});
                setView2TagsCount(Object.keys(merged).length > 0 ? merged : null);
            } catch (err) {
                Logger.error("Error fetching view 2 tags:", err);
                setView2TagsCount(null);
            }
        };
        fetchView2Tags();
    }, [statsViewMode, activeStep]);

    // Fetch available tags for the current step's state (language from admin.content.lang)
    useEffect(() => {
        const fetchTags = async () => {
            setTagsLoading(true);
            try {
                const language = getAdminContentLanguage();
                const state = steps[activeStep]?.state ?? undefined;
                const tags = await CreatorGridService.getGridTags(language, state);
                const list = Array.isArray(tags) ? tags : [];
                list.sort((a, b) => {
                    const countA = typeof a === "object" && a != null && typeof a.count === "number" ? a.count : 0;
                    const countB = typeof b === "object" && b != null && typeof b.count === "number" ? b.count : 0;
                    return countA - countB;
                });
                setAvailableTags(list);
            } catch (error) {
                Logger.error('Error fetching tags:', error);
                setAvailableTags([]);
            } finally {
                setTagsLoading(false);
            }
        };

        fetchTags();
    }, [activeStep]);

    // Handle type filter change with localStorage persistence
    const handleTypeChange = (newType) => {
        setSelectedType(newType);
        saveFilterType(newType);
    };

    // Handle tags filter change with localStorage persistence
    const handleTagsChange = (newTags) => {
        setSelectedTags(newTags);
        saveFilterTags(newTags);
    };

    // Handle level filter change with localStorage persistence
    const handleLevelChange = (newLevel) => {
        setSelectedLevel(newLevel);
        saveFilterLevel(newLevel);
    };

    // Handle free/paid filter change with localStorage persistence
    const handleFreeChange = (newFree) => {
        setSelectedFree(newFree);
        saveFilterPayment(newFree);
    };

    const handleGridSizeChange = (newSize) => {
        const size = normalizeStoredGridSize(newSize);
        setSelectedGridSize(size);
        saveFilterSize(size);
    };

    useEffect(() => {
        const tagParam = searchParams.get("tag");
        if (!tagParam || !tagParam.trim()) {
            return;
        }
        const next = [tagParam.trim()];
        setSelectedTags((prev) => {
            if (prev.length === 1 && prev[0] === next[0]) {
                return prev;
            }
            saveFilterTags(next);
            return next;
        });
    }, [searchParams]);

    // Handle URL parameters for activeStep
    useEffect(() => {
        const activeStepParam = searchParams.get('activeStep');
        if (activeStepParam !== null) {
            const stepIndex = parseInt(activeStepParam, 10);
            if (stepIndex >= 0 && stepIndex < steps.length && stepIndex !== activeStep) {
                setActiveStep(stepIndex);
            }
        }
    }, [searchParams, activeStep]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };


    const handleStepClick = (stepIndex) => {
        // Only update if step actually changed
        if (stepIndex === activeStep) {
            return;
        }
        
        // Update URL to reflect the new step
        navigate(`/admin/puzzle/grid_board?activeStep=${stepIndex}`, { replace: true });
        
        // Set active step
        setActiveStep(stepIndex);
    };

    const handlePuzzleClick = (transformedPuzzle) => {
        navigate(gridBoardDetailPath(transformedPuzzle, activeStep));
    };

    return (
        <Box sx={{ width: "100%" }}>
            <PuzzleWorkflowStepper
                currentState={steps[activeStep].state}
                onStepClick={handleStepClick}
                size="small"
                align="left"
                puzzleType="GRID"
            />

            <Box>
                {/* Group-by-state stats with view toggle (1 = 4 cards, 2 = 2 cards) */}
                <Box sx={{ mb: 3, display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            flexShrink: 0
                        }}
                    >
                        <Button
                            variant={statsViewMode === 1 ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setStatsViewModeAndSave(1)}
                            sx={{ minWidth: 36 }}
                        >
                            1
                        </Button>
                        {activeStep === 0 && (
                            <Button
                                variant={statsViewMode === 2 ? "contained" : "outlined"}
                                size="small"
                                onClick={() => setStatsViewModeAndSave(2)}
                                sx={{ minWidth: 36 }}
                            >
                                2
                            </Button>
                        )}
                    </Box>
                    {groupByStateLoading ? (
                        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                                Loading stats...
                            </Typography>
                        </Box>
                    ) : groupByStateStats && Object.keys(groupByStateStats).length > 0 ? (
                        <Grid container spacing={2} sx={{ flex: 1 }}>
                            {statsViewMode === 1
                                ? DISPLAY_STATES.map((state) => (
                                      <StateStatsCard
                                          key={state}
                                          state={state}
                                          stateData={groupByStateStats[state]}
                                          typesFilter={GRID_PUZZLE_TYPES}
                                          showTags={false}
                                          showFreePaidBreakdown={state === PUBLISHED}
                                      />
                                  ))
                                : [
                                      {
                                          state: "READY_TO_REVIEW_REVIEWED_PUBLISHED",
                                          stateLabelOverride: "READY TO REVIEW / REVIEWED / PUBLISHED",
                                          stateData: (() => {
                                              const merged = mergeStateStats(groupByStateStats, [
                                                  READY_TO_REVIEW,
                                                  REVIEWED,
                                                  PUBLISHED
                                              ]);
                                              // Use view2TagsCount as fallback when countByTag is empty
                                              if (view2TagsCount && Object.keys(merged.countByTag || {}).length === 0) {
                                                  return { ...merged, countByTag: view2TagsCount };
                                              }
                                              return merged;
                                          })()
                                      }
                                  ].map(({ state, stateData, stateLabelOverride }) => (
                                      <StateStatsCard
                                          key={state}
                                          state={state}
                                          stateData={stateData}
                                          typesFilter={GRID_PUZZLE_TYPES}
                                          stateLabelOverride={stateLabelOverride}
                                          showTags={true}
                                          tagsOnly={true}
                                          excludeTags={["bedrock"]}
                                          gridSize={{ xs: 12 }}
                                          onTagClick={(tag) => handleTagsChange([tag])}
                                          hint="Choose tags with low counts and create puzzles for them—we need more puzzles in those categories."
                                      />
                                  ))}
                        </Grid>
                    ) : null}
                </Box>

                <Step0SelectDraft
                    key={`step-${activeStep}`} // Add key to force re-render when step changes
                    step={activeStep}
                    stepState={steps[activeStep].state}
                    selectedGenreId={selectedGenreId}
                    selectedType={selectedType}
                    selectedTags={selectedTags}
                    availableTags={availableTags}
                    tagsLoading={tagsLoading}
                    selectedLevel={selectedLevel}
                    selectedFree={selectedFree}
                    selectedGridSize={selectedGridSize}
                    onTypeChange={handleTypeChange}
                    onTagsChange={handleTagsChange}
                    onLevelChange={handleLevelChange}
                    onFreeChange={handleFreeChange}
                    onGridSizeChange={handleGridSizeChange}
                    initialKeyword={savedKeyword}
                    onKeywordChange={(value) => {
                        setSavedKeyword(value ?? "");
                        saveFilterKeyword(value);
                    }}
                    handlePuzzleClick={handlePuzzleClick}
                />

                <Box sx={{ display: "flex", flexDirection: "row", mt: 2 }}>
                    <Button
                        color="inherit"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                    >
                        Back
                    </Button>
                    <Box sx={{ flex: "1 1 auto" }} />
                    {activeStep < steps.length - 1 && (
                        <Button onClick={handleNext} variant="contained">
                            Next
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Current Step: {activeStep + 1} of {steps.length} - {steps[activeStep].label}
                </Typography>
            </Box>

            {/* Publishing Policy Dialog */}
            <PublishingPolicyDialog
                open={policyDialogOpen}
                onClose={() => setPolicyDialogOpen(false)}
            />
        </Box>
    );
}

